/**
 * In-memory store for the document-download step-up token.
 *
 * The token is intentionally kept in module-scoped memory only (NOT in
 * sessionStorage / localStorage) so that it is naturally cleared when the
 * user closes the browser tab. The backend independently caps the lifetime
 * at 8 hours.
 *
 * The store also brokers communication between the secure-download wrapper
 * and the DocumentMfaProvider: the provider registers a "prompter" callback
 * on mount, and the secure-download wrapper invokes it via `requestStepUp`
 * whenever the API returns 401 with code `step_up_required`.
 */

type Prompter = () => Promise<void>;

let stepUpToken: string | null = null;
let prompter: Prompter | null = null;
let pendingPrompt: Promise<void> | null = null;

export const documentMfaStore = {
  getToken: (): string | null => stepUpToken,

  setToken: (token: string | null): void => {
    stepUpToken = token;
  },

  clearToken: (): void => {
    stepUpToken = null;
  },

  setPrompter: (fn: Prompter | null): void => {
    prompter = fn;
  },

  /**
   * Triggers the step-up modal and resolves once the user has successfully
   * verified a code. Concurrent callers share the same in-flight prompt.
   */
  requestStepUp: async (): Promise<void> => {
    if (!prompter) {
      throw new Error('Document MFA prompter is not registered.');
    }

    if (pendingPrompt) {
      return pendingPrompt;
    }

    pendingPrompt = (async () => {
      try {
        await prompter!();
      } finally {
        pendingPrompt = null;
      }
    })();

    return pendingPrompt;
  },
};
